//@ts-check
import type { ReactNode } from 'react';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { isArray } from 'lodash-es';
import {
  CartData,
  changeItemQuantity,
  removeLineItems,
  SetLineItemProperties,
  CartItem,
  ADDED_TO_CART_EVENT,
  ADD_TO_CART_EVENT_DATA,
  CHECKOUT_TEXT,
  PROCESSING_TEXT,
  isGiftCardWithRecipient,
  showSingleQuantityAmount,
  isCartApiCheckoutButtonEnabled,
  isLoopReturnsActive,
  submitLoopStore,
} from '../../../../utils/cart';
import { ALO_MOVES_30_GWP, ALO_MOVES_REAL_SKU } from '../../../../utils/constants';
import { UrgencyMessage } from '../../../../templates/product/components/ScarcityMessaging/UrgencyMessage';
import { observer } from 'mobx-react-lite';
import {
  computeDisplayForOptionWithValues,
  formatShopifyPrice,
  getStrikeThroughLinePrice,
  QuantityChangeButtons,
} from './helpers';
import { computeCartGroupData } from '../../../../utils/cart/computeCartGroupData';
import { computeCartItemGroups } from './helpers';
import ButtonWithProgress from 'src/scripts/templates/_shared/ui-components/YogaUI/ButtonWithProgress';
import PurchaseLimitMessaging, {
  extractPurchaseLimitTagData,
} from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { checkIfItemCanBeSoldOut, getOnlineSalePricing, preorderDateRangeCopy } from 'src/scripts/utils/helpers';
import { CosmeticCartItem } from './CartPage';
import { memorizeGiftRemoval } from './GWP/gwpUtils';
import { fireHeapRemovalGWPevent } from 'src/scripts/utils/heap-events';
import RemoveFromCartButton from './RemoveFromCartButton';
import { useCheckIfUserIsInUS } from 'src/scripts/templates/_shared/react-hooks';
import { ApolloProvider } from '@apollo/client';
import { ShippingCountryState } from '../../../../utils/shipping';
import { CartApolloClient } from 'src/scripts/utils/cart/client';
import { refundClaimedReward } from './Loyalty/loyaltyUtils';
import { SHOP_ROOT, prependLocaleToPath } from 'src/scripts/utils/localization';
import CartCheckoutButton from './CartCheckoutButton';

const MiniCart = ({ cartData }: { cartData: CartData }) => {
  // Animation
  const miniCartRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);

  // ? [REVISIT] this is no longer relevant with the introduction of ATC modal, the UX needs refactored so when the ATC modal isn't relevant this can be used at the fallback experience
  useEffect(() => {
    let timeout: undefined | number;
    const closeOnOutsideclick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (miniCartRef.current && !miniCartRef.current.contains(target)) close();
    };
    document.addEventListener(ADDED_TO_CART_EVENT, (event) => {
      const addToCartEvent = event as CustomEvent<ADD_TO_CART_EVENT_DATA>;
      const addedASet = addToCartEvent.detail.variantIds.length > 1;
      if (addedASet) {
        setIsOpen(true);
        if (timeout) clearTimeout(timeout);
        timeout = Number(setTimeout(close, 3000));
      }
    });
    document.addEventListener('scroll', close);
    document.addEventListener('mousedown', closeOnOutsideclick);
    return () => {
      document.removeEventListener('scroll', close);
      document.removeEventListener('mousedown', closeOnOutsideclick);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (cartData.subtotal === 0 && (!cartData.regularItems || cartData.regularItems.length === 0)) {
      document.body.classList.add('disable-checkout-mode');
    } else {
      document.body.classList.remove('disable-checkout-mode');
    }
  }, [cartData]);

  const { totalCartItemsQty } = cartData;

  return (
    <ApolloProvider client={CartApolloClient}>
      <div ref={miniCartRef} className={`item__cart cart__link ${isOpen ? 'open' : ''}`} id="MiniCart">
        <MiniCartShellUI cartItemCount={totalCartItemsQty}>
          {/* // ? [REVISIT] id="mini-cart" still needed */}
          <div id="mini-cart" className="cart-block" onMouseLeave={close}>
            {totalCartItemsQty > 0 ? <CartWithItemsUI cartData={cartData} /> : <EmptyCartUI />}
          </div>
        </MiniCartShellUI>
      </div>
    </ApolloProvider>
  );
};

export { MiniCart };

// helper functions / sub components
let CartWithItemsUI = ({ cartData }: { cartData: CartData }) => {
  const { subtotal, count } = cartData;

  return (
    <React.Fragment>
      <CartItems cartData={cartData} />
      <div className="mini-cart-sticky-checkout-details">
        <div className="cart-price">
          <div className="cart-price-name">
            <div className="link__to-cart--wrapper">
              <a href="/cart" className="link__to-cart in-cart--1 body-text-link">
                View Bag (<span className="count-bottom">{count}</span>)
              </a>
            </div>
          </div>
          <div className="cart-price-value subtotal" data-currency>
            <span className="colon">Subtotal</span> <span>{formatShopifyPrice(subtotal)}</span>
          </div>
        </div>
        {ShippingCountryState.country && ShippingCountryState.country?.dutiesCopy ? (
          <p className="small-p duties-copy-mini-cart">({ShippingCountryState.country?.dutiesCopy})</p>
        ) : null}
        <div className="mini-cart__form-wrapper">
          {isCartApiCheckoutButtonEnabled ? (
            <div className="mini-cart__checkout-form mb-3">
              <CartCheckoutButton />
            </div>
          ) : (
            <MiniCartCheckoutForm />
          )}
        </div>
        <div className="text-center pb-4 d-flex urgency__notice">
          <span className="alo-clock-icon"></span>
          <div className="text">Items are not held and may sell out</div>
        </div>
      </div>
    </React.Fragment>
  );
};
CartWithItemsUI = observer(CartWithItemsUI);

const CartItems = ({ cartData }: { cartData: CartData }) => {
  const cartItemGroupsToRender = computeCartItemGroups(cartData);
  const giftItemsAndLoyaltyRewards = cartData && cartData.giftItemsAndLoyaltyRewards;
  return (
    <div className="cart-block-inner">
      <div className="cart-items">
        {cartItemGroupsToRender.map((group) => {
          if (!isArray(group)) {
            //Non-Set
            const item = group;
            const { key, product_title, price, url, quantity, tags, amountPerQuantity } = item;
            const onlineSalePrice = tags ? getOnlineSalePricing(tags, price) : undefined;
            const priceToRender = onlineSalePrice ? onlineSalePrice * 100 : price;
            return (
              <CartItemShell
                key={key}
                href={url}
                title={product_title}
                price={showSingleQuantityAmount(quantity, amountPerQuantity, priceToRender * quantity)}
                strikeThroughPrice={getStrikeThroughLinePrice(item)}
              >
                <VariantDisplayUI
                  item={item}
                  hideQuantity={item.sku === ALO_MOVES_REAL_SKU || isGiftCardWithRecipient(item)}
                  extraActions={
                    <RemoveFromCartButton
                      onClick={async () => {
                        await changeItemQuantity(item.key, 0);
                      }}
                    />
                  }
                />
              </CartItemShell>
            );
          } else {
            //Set
            // so much duplicated logic 🤮, between Cart UI & MiniCart UI
            //@ts-ignore
            const properties: SetLineItemProperties = group[0].properties;
            const { _setName, _setUrl, _setUid, _setStrikeThroughPrice } = properties;
            let [price, displayPrice] = group.reduce(
              ([priceSum, displayPriceSum], item) => {
                const { quantity, price, displayPrice = price, tags, original_price, amountPerQuantity } = item;
                const onlineSalePrice = tags ? getOnlineSalePricing(tags, price) : undefined;
                const currentDisplayPrice = onlineSalePrice ? original_price : displayPrice;
                return onlineSalePrice
                  ? [
                      priceSum +
                        showSingleQuantityAmount(quantity, amountPerQuantity, onlineSalePrice * 100 * quantity),
                      displayPriceSum + currentDisplayPrice * quantity,
                    ]
                  : [
                      priceSum + showSingleQuantityAmount(quantity, amountPerQuantity, price * quantity),
                      displayPriceSum + displayPrice * quantity,
                    ];
              },
              [0, 0]
            );
            if (_setStrikeThroughPrice) displayPrice = _setStrikeThroughPrice;
            const groupDataForUI = properties._setForcedMatchingSets ? computeCartGroupData(group) : group;
            const variantsList = groupDataForUI.map((setItem) => setItem.id);
            return (
              <CartItemShell
                className="set-cart-item"
                key={_setUid}
                title={_setName}
                href={_setUrl + '?variants=' + variantsList.join(',')}
                price={price}
                strikeThroughPrice={price === displayPrice ? null : displayPrice}
              >
                {groupDataForUI.map((setItem, index, arr) => (
                  <VariantDisplayUI
                    key={setItem.key}
                    item={setItem}
                    variantTitle={setItem.product_title}
                    variantTitleHref={setItem.url}
                    hideQuantity={true}
                    extraActions={
                      index === arr.length - 1 && (
                        <RemoveFromCartButton
                          onClick={async () => {
                            await removeLineItems(group.map((item) => item.key));
                          }}
                        />
                      )
                    }
                  />
                ))}
              </CartItemShell>
            );
          }
        })}
        <>
          {giftItemsAndLoyaltyRewards.length > 0 &&
            giftItemsAndLoyaltyRewards
              .filter((item) => item.sku !== ALO_MOVES_30_GWP)
              .map((item) => {
                const { key, product_title, variant_id, final_line_price, properties } = item;
                return (
                  <CartItemShell
                    key={key}
                    title={product_title}
                    href={undefined}
                    price={final_line_price}
                    strikeThroughPrice={null}
                    isGift={true}
                    isBeautyGift={false}
                    isAloMoves30GWP={false}
                  >
                    <CosmeticCartItem
                      key={key}
                      item={item}
                      extraActions={
                        <>
                          <span style={{ flex: 1 }}></span>
                          <RemoveFromCartButton
                            onClick={async () => {
                              memorizeGiftRemoval(variant_id);
                              if (item.properties?._loyalty_reward_claim_id) {
                                await refundClaimedReward(
                                  item.properties?._loyalty_reward_claim_id,
                                  item.variant_id.toString(),
                                  key
                                );
                              } else {
                                await changeItemQuantity(key, 0, false, properties);
                                if (properties._isGWP)
                                  fireHeapRemovalGWPevent({
                                    productTitle: product_title,
                                    variantId: variant_id.toString(),
                                  });
                              }
                            }}
                          />
                        </>
                      }
                      isMiniBag={true}
                    />
                  </CartItemShell>
                );
              })}
        </>
      </div>
    </div>
  );
};

const CartItemShell = ({
  title,
  price,
  strikeThroughPrice,
  isGift = false,
  isBeautyGift = false,
  href,
  children,
  className = '',
  isAloMoves30GWP = false,
}: {
  title: string;
  price: number;
  strikeThroughPrice: number | null;
  isGift?: boolean;
  isBeautyGift?: boolean;
  href: string | undefined;
  children: ReactNode;
  className?: string;
  isAloMoves30GWP?: boolean;
}) => {
  return (
    <div
      // push the Alo Moves 30 GWP to the bottom of the cart
      style={isAloMoves30GWP ? { order: 1 } : {}}
      className={`CartItemShell cart-item ${className} ${
        isGift ? `cart-gift-item` : isAloMoves30GWP ? 'cart-alo-moves-free-30-item' : ''
      }`}
    >
      <div className="title__wrapper">
        <p className="cart-item-title body semibold">{href ? <a href={href}>{title}</a> : <span>{title}</span>}</p>
        {/* // ? [REVISIT] to check if this is done right? do we sell free items? */}
        <div className="cart-item-tool">
          <div className="cart-item-price body-small">
            {price === 0 ? (
              <span className="currency-formatting body-small black uppercase">Free</span>
            ) : !strikeThroughPrice ? (
              <span className="currency-formatting body-small black uppercase" data-currency>
                {formatShopifyPrice(price)}
              </span>
            ) : (
              <React.Fragment>
                <span className="currency-formatting body-small red" data-currency>
                  {formatShopifyPrice(price)}
                </span>
                <span className="text-muted body-small grey strikethrough">
                  {formatShopifyPrice(strikeThroughPrice)}
                </span>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

const VariantDisplayUI = ({
  item,
  hideQuantity = false,
  extraActions,
  variantTitle,
  variantTitleHref,
  className = '',
}: {
  // ? bad name
  item: CartItem;
  hideQuantity?: boolean;
  extraActions?: ReactNode;
  // ? [REVISIT] just pass in a subcomponent?
  variantTitle?: string;
  variantTitleHref?: string;
  className?: string;
}) => {
  // ? some of these properties shouldn't be here
  const {
    title,
    image,
    options_with_values,
    quantity,
    showFinalSaleTreatment,
    product_type,
    gift_card,
    key,
    showExchangeOnlyMessage,
    estimatedShipDate,
    quantityAvailable,
    tags,
  } = item;

  let quantityLabelForSets = quantity > 1 && hideQuantity ? ` ✕ ${quantity}` : null;

  const unlimitedInventory = tags ? gift_card || !checkIfItemCanBeSoldOut(tags) : false;

  const purchaseLimitTagData = extractPurchaseLimitTagData(tags);
  const userIsInUS = useCheckIfUserIsInUS();
  const preorderCopy = preorderDateRangeCopy(estimatedShipDate);

  return (
    <div className={`VariantDisplayUI ${className}`}>
      <div className="cart-item-img">
        <img src={image} alt={title} />
      </div>
      <div className="cart-item-descr">
        {variantTitle && (
          <a href={variantTitleHref} className="title">
            {variantTitle}
            {quantityLabelForSets}
          </a>
        )}

        <div className="text body-small">
          {computeDisplayForOptionWithValues({
            options: options_with_values,
            productType: product_type,
            tags,
          }).map(({ label, value }) => (
            <p className="cart-item-options body-small" key={label}>
              <span>{label}:</span> <span>{value}</span>
            </p>
          ))}
          {preorderCopy && (
            <p className="mini-cart-preorder-info body-small red">
              <span>Preorder expected to ship by: </span>
              <span>{preorderCopy}</span>
            </p>
          )}
          {!unlimitedInventory && <UrgencyMessage quantityLeft={quantityAvailable} className="minicart" />}
          {showFinalSaleTreatment && <div className="body-small red uppercase">Final Sale</div>}
          {userIsInUS && showExchangeOnlyMessage && <div className="body-small red uppercase">Exchange Only</div>}
          {purchaseLimitTagData ? (
            <PurchaseLimitMessaging purchaseLimitTagData={purchaseLimitTagData} messageType={'default'} />
          ) : null}
        </div>

        <div className="mini-cart__actions">
          {!hideQuantity ? (
            <QuantityChangeButtons
              buttonClassName={'d-none d-lg-block'}
              isUnlimited={unlimitedInventory}
              itemKey={key}
              quantity={quantity}
              purchaseLimitTagData={purchaseLimitTagData}
              quantityAvailable={quantityAvailable}
            />
          ) : (
            <div style={{ flex: 1 }} />
          )}
          {extraActions}
        </div>
      </div>
    </div>
  );
};

const EmptyCartUI = React.memo(() => (
  <div className="cart-block-inner cart-block-empty">
    <h6 className="cart-title pt-4 h6 center">Your Bag Is Empty</h6>
    <div className="cart-items">
      <p className="cart-note body-small center">Free worldwide shipping and easy returns</p>
    </div>
  </div>
));

type MiniCartShellUIProps = {
  children: React.ReactNode;
  cartItemCount: number;
};

const MiniCartShellUI = React.memo(({ children, cartItemCount }: MiniCartShellUIProps) => (
  <Fragment>
    <a href="/cart" className="main-navbar__icon main-navbar__icon--bag" aria-label="Cart">
      <div className="cart-icon-wrapper">
        <span className="icon-bag-static"></span>
        <span className="cart-count-text">{cartItemCount || ''}</span>
      </div>
    </a>
    <div className="rect__wrapper">
      <div className="empty__rect" />
      <div className="empty__rect--small" />
    </div>
    {children}
  </Fragment>
));

const MiniCartCheckoutForm = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const isLoopActive = isLoopReturnsActive();
  return (
    <form
      action={`${SHOP_ROOT}checkout`}
      method="post"
      noValidate
      className="mini-cart__checkout-form"
      onSubmit={(e) => {
        setIsProcessing(true);
        try {
          // Fix for LND-1706: Navigating to checkout from the mini-bag and clicking back to cart kicks user out of translated experience
          fetch(prependLocaleToPath('/cart'), { method: 'post' });
        } catch (error) {
          console.log(error);
        } finally {
          setIsProcessing(false);
        }
      }}
    >
      <ButtonWithProgress
        options={{
          showProgressAnimation: isProcessing,
          cta: isProcessing ? PROCESSING_TEXT : CHECKOUT_TEXT,
          // To trigger Loop-submit on click if loopreturn is active.
          type: isLoopActive ? 'button' : 'submit',
          name: 'checkout',
          wrapperClasses: 'cart-buttons checkout-enabled',
          buttonTypeClass: 'button-primary',
          buttonClasses: 'button-primary btn-block btn__checkout',
          disableShrink: true,
          onClick: () => {
            if (isLoopActive) {
              submitLoopStore();
            }
          },
        }}
      />
      {/*Below is a "dummy" checkout button that only appears when checkout is disabled*/}
      <div className="cart-buttons">
        <button className="button-primary btn-block mb-4 btn__checkout checkout-disabled" type="button" disabled>
          CHECKOUT
        </button>
      </div>
    </form>
  );
};
